import React, { useState, useEffect } from 'react';
import * as MuiIcons from '@mui/icons-material';
import clsx from 'clsx';
import { Icon } from '../../lib/icons';
import { useSiteData } from '../../data/DataProviders/SiteDataProvider';
import { isDataLoaded, isDataLoading, isDataError } from '../../types/data-providers';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { OverviewCard } from '../OverviewCard/OverviewCard';
import { SiteCardContentLoader } from './SiteCardContentLoader';
import { AppRoute } from '../../routes';
import * as _ from 'lodash';
import { getPrecisionValue } from '../../lib/precision-helper';
import { useSignalRSiteData } from '../../data/DataProviders/SignalRSiteDataProvider';
import { ChannelDatum } from '../../types';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getStatus = (siteData: ReturnType<typeof useSiteData>, alarmCount: number) => {
    if (isDataLoading(siteData) || isDataError(siteData)) {
        return {
            hasAlarm: undefined,
            hasEvents: undefined,
            isOffline: undefined,
        };
    }

    return {
        hasAlarm: alarmCount > 0 || siteData.value.online !== true,
        hasEvents: siteData.value.eventCount !== undefined && siteData.value.eventCount > 0,
        isOffline: siteData.value.online === false,
    };
};

type Props = {
    id: string;
    name: string;
    alarmCount: number;
    locationId: string;
    deviceCount: number;
};

export const SiteCard: React.FC<Props> = (props: Props) => {
    const { t } = useLanguageLocale();
    const siteData = useSiteData();
    const siteRealTimeData = useSignalRSiteData();
    const { hasAlarm } = getStatus(siteData, props.alarmCount);
    const [hasRecentAlarm, setRecentAlarm] = useState(false);
    const [alarmCount, setAlarmCount] = useState(0);
    const [status, setStatus] = useState(false);
    const [channlValue, setChannelValue] = useState<ChannelDatum[]>([]);
    const offlineCountdata = isDataLoaded(siteData) ? siteData.value.offlinedevicecount : 0;

    useEffect(() => {
        if (isDataLoaded(siteData)) {
            setRecentAlarm(hasAlarm || false);
            setAlarmCount(props.alarmCount);
            setStatus(siteData.value.online || false);
            setChannelValue(_.take(siteData.value.channelData, 2));
            // offlineCountdata
        }
    }, [siteData, hasAlarm]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isDataLoaded(siteData) && siteRealTimeData) {
            if (siteRealTimeData.alarmCount && siteRealTimeData.alarmCount !== null) {
                if (siteRealTimeData.alarmStatus && siteRealTimeData.alarmStatus.length > 0) {
                    setRecentAlarm(siteRealTimeData.alarmCount > 0 || siteData.value.online !== true);
                    setAlarmCount(siteRealTimeData.alarmCount);
                }
            }
            if (siteRealTimeData.connectionStatus) setStatus(siteRealTimeData.connectionStatus);
            if (siteRealTimeData.channelData && siteRealTimeData.channelData.length > 0) {
                _.filter(siteData.value.channelData, (channelData) => {
                    _.filter(siteRealTimeData.channelData, (updatedChannel) => {
                        if (
                            channelData.id === updatedChannel.channelId &&
                            channelData.deviceId === updatedChannel.deviceId
                        )
                            channelData.value = getPrecisionValue(updatedChannel.value);
                    });
                });
                setChannelValue(siteData.value.channelData);
            }
        }
    }, [siteRealTimeData, siteData]);

    const subtitle = isDataLoaded(siteData)
        ? alarmCount > 0
            ? t('DASHBOARD.LOCATION_CARD.HEADER.SUBTITLE.ALARM', { count: alarmCount })
            : t('DASHBOARD.LOCATION_CARD.HEADER.SUBTITLE.NORMAL')
        : '';

    const headerInfo = isDataLoaded(siteData)
        ? t('DASHBOARD.SITE_CARD.HEADER.INFO.DEVICE', { count: props.deviceCount ? props.deviceCount : 0 })
        : '';

    const navigationLabel = t('DASHBOARD.SITE_CARD.FOOTER.VIEW_LOCATION');
    const navigationTargetAppRoute: AppRoute = {
        type: 'OverviewSite',
        locationId: props.locationId,
        siteId: props.id,
    };

    const data = (isDataLoaded(siteData)
      ? siteData.value.online === false
      : !status)
        ? {
              label: t('DASHBOARD.SITE_CARD.BODY.OFFLINE_DEVICE', {
                  count: isDataLoaded(siteData) ? siteData.value.offlinedevicecount : 0,
              }),
              bold: (offlineCountdata || 0) > 0,
              icon: MuiIcons.CloudOff,
              className: clsx({ errorColor: offlineCountdata }),
          }
        : {
              label: t('DASHBOARD.SITE_CARD.BODY.ONLINE'),
              bold: !status,
              icon: status ? Icon.CloudDone : Icon.CloudOff,
              className: clsx({ primaryColor: !status }),
          };

    const statusItems = isDataLoaded(siteData)
        ? [
              {
                  label: t('DASHBOARD.SITE_CARD.BODY.ALARMS', { count: alarmCount }),
                  bold: (alarmCount || 0) > 0,
                  icon: MuiIcons.NotificationsActive,
                  className: clsx({ errorColor: hasRecentAlarm }),
              },
              //TODO: comment as per MVP scope , may be needed post MVP
              //   {
              //       label: tNoData('DASHBOARD.SITE_CARD.BODY.EVENTS', siteData.value.eventCount),
              //       bold: (siteData.value.eventCount || 0) > 0,
              //       icon: MuiIcons.Info,
              //       className: clsx({ primaryColor: hasEvents }),
              //   },
              {
                  label: data.label,
                  bold: data.bold,
                  icon: data.icon,
                  className: data.className,
              },
          ]
        : [];

    return (
        <div data-testid="site-card">
            <OverviewCard
                id={props.id}
                title={props.name}
                subtitle={subtitle}
                headerInfo={headerInfo}
                hasAlarm={hasRecentAlarm}
                dataState={siteData.state}
                loadingComponent={SiteCardContentLoader}
                statusItems={statusItems}
                channelData={channlValue}
                navigationLabel={navigationLabel}
                navigationTargetAppRoute={navigationTargetAppRoute}
                statusDataState={siteData.state}
            />
        </div>
    );
};
